import React from 'react';

interface TeamOverviewTabProps {
    selectedTeam: any;
    teamInsights: any;
    rankings: any;
    days: number;
    isDark: boolean;
    handleAthleteDoubleClick: (athleteId: number) => void;
}

const TeamOverviewTab: React.FC<TeamOverviewTabProps> = ({
    selectedTeam,
    teamInsights,
    rankings,
    days,
    isDark,
    handleAthleteDoubleClick
}) => {
    function getRecoveryColor(value: number) {
        if (value >= 67) return '#67AEE6'; // High
        if (value >= 34) return '#F5D76E'; // Medium
        return '#EF4444'; // Low
    }

    // Add color helpers for sleep and strain:
    function getSleepColor() {
      return '#7BA1BB';
    }
    function getStrainColor() {
      return '#0093E7';
    }

    return (
        <div className="space-y-8">
            {/* Team Health Score & Key Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className={`bg-gradient-to-br ${isDark ? 'from-blue-900/20 to-blue-800/20' : 'from-blue-50 to-blue-100'} rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} transform hover:scale-105 transition-all duration-300 shadow-xl`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white text-xl">
                            🏃‍♂️
                        </div>
                        <div className="text-right">
                                            <div className="text-2xl font-bold cgm-text-primary">{selectedTeam.recovery_data?.summary.total_athletes || 0}</div>
                <div className="text-sm cgm-text-secondary font-medium">Total Athletes</div>
                        </div>
                    </div>
                </div>

                <div className={`bg-gradient-to-br ${isDark ? 'from-green-900/20 to-green-800/20' : 'from-green-50 to-green-100'} rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} transform hover:scale-105 transition-all duration-300 relative group shadow-xl`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center text-white text-xl">
                            💚
                        </div>
                        <div className="text-right">
                            <div className="flex items-center space-x-2 justify-end">
                                <div className="text-2xl font-bold cgm-text-primary">{teamInsights.teamHealthScore}</div>
                                <div className="relative">
                                    <div className={`w-5 h-5 rounded-full ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'} flex items-center justify-center cursor-help transition-colors duration-200`}>
                                        <svg className="w-3 h-3 cgm-text-primary" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                        </svg>
                                    </div>

                                    {/* Hover Tooltip */}
                                    <div className={`absolute bottom-full right-0 mb-2 w-80 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} border rounded-lg shadow-xl p-4 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 z-50`}>
                                        <div className="space-y-3">
                                            <h4 className="text-sm font-semibold cgm-text-primary border-b border-gray-200 dark:border-gray-600 pb-2">How it's calculated:</h4>
                                            <div className="space-y-2 text-xs cgm-text-secondary">
                                                <div className="flex items-center justify-between">
                                                    <span>• Average Recovery Score</span>
                                                    <span className="font-medium">{rankings.recovery.length > 0 ?
                                                        `${(rankings.recovery.reduce((sum: number, r: any) => sum + r.latestRecovery, 0) / rankings.recovery.length).toFixed(1)}%` : 'N/A'}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span>• Average Sleep Quality</span>
                                                    <span className="font-medium">{rankings.sleep.length > 0 ?
                                                        `${(rankings.sleep.reduce((sum: number, s: any) => sum + s.averageQuality, 0) / rankings.sleep.length).toFixed(1)}%` : 'N/A'}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span>• Data Completeness</span>
                                                    <span className="font-medium">{
                                                        selectedTeam.recovery_data?.summary.total_athletes
                                                            ? `${((selectedTeam.recovery_data.athletes_data.filter((a: any) => a.whoop_connected &&
                                                                (rankings.recovery.find((r: any) => r.name === a.athlete_name)?.dataPoints || 0) >= Math.min(days * 0.3, 7)).length /
                                                                selectedTeam.recovery_data.athletes_data.filter((a: any) => a.whoop_connected).length) * 100).toFixed(1)}%`
                                                            : 'N/A'
                                                    }</span>
                                                </div>
                                                <div className="text-xs cgm-text-secondary mt-1 italic">
                                                    Athletes with ≥{Math.min(days * 0.3, 7).toFixed(0)} days of data
                                                </div>
                                            </div>
                                            <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                                                <div className="text-xs cgm-text-secondary">
                                                    <strong>Score Ranges:</strong><br />
                                                    <span className="text-green-600">70-100 Excellent</span> •
                                                    <span className="text-yellow-600">50-69 Good</span><br />
                                                    <span className="text-orange-600">30-49 Needs Attention</span> •
                                                    <span className="text-red-600">0-29 Critical</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tooltip Arrow */}
                                        <div className={`absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent ${isDark ? 'border-t-gray-800' : 'border-t-white'}`}></div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-sm cgm-text-secondary font-medium">Team Health Score</div>
                        </div>
                    </div>
                </div>

                <div className={`bg-gradient-to-br ${teamInsights.athletesInTheRed.length > 0 ? (isDark ? 'from-red-900/20 to-red-800/20' : 'from-red-50 to-red-100') : (isDark ? 'from-emerald-900/20 to-emerald-800/20' : 'from-emerald-50 to-emerald-100')} rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} transform hover:scale-105 transition-all duration-300 shadow-xl`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className={`w-12 h-12 bg-gradient-to-r ${teamInsights.athletesInTheRed.length > 0 ? 'from-red-500 to-red-600' : 'from-emerald-500 to-emerald-600'} rounded-xl flex items-center justify-center text-white text-xl`}>
                            {teamInsights.athletesInTheRed.length > 0 ? '🚨' : '✅'}
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold cgm-text-primary">{teamInsights.athletesInTheRed.length}</div>
                            <div className="text-sm cgm-text-secondary font-medium">In the Red</div>
                        </div>
                    </div>
                </div>

                <div className={`bg-gradient-to-br ${isDark ? 'from-amber-900/20 to-orange-800/20' : 'from-amber-50 to-orange-100'} rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} transform hover:scale-105 transition-all duration-300 shadow-xl`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center text-white text-xl">
                            📊
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold cgm-text-primary">
                                {selectedTeam.recovery_data?.summary.total_athletes
                                    ? `${Math.round((selectedTeam.recovery_data.summary.athletes_with_data / selectedTeam.recovery_data.summary.total_athletes) * 100)}%`
                                    : '0%'}
                            </div>
                            <div className="text-sm cgm-text-secondary font-medium">Data Coverage</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Team Trends Analysis */}
            {teamInsights.teamTrends && (
                <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl relative group`}>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-xl">
                                📈
                            </div>
                            <div>
                                <h3 className="text-xl font-bold cgm-text-primary">Team Performance Trends</h3>
                                <p className="text-sm cgm-text-secondary">Overall team performance direction over the {days}-day period</p>
                            </div>
                        </div>
                        <div className="relative">
                            <div className={`w-6 h-6 rounded-full ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'} flex items-center justify-center cursor-help transition-colors duration-200`}>
                                <svg className="w-4 h-4 cgm-text-primary" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                </svg>
                            </div>
                            {/* Hover Tooltip */}
                            <div className={`absolute bottom-full right-0 mb-2 w-80 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} border rounded-lg shadow-xl p-4 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 z-50`}>
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold cgm-text-primary border-b border-gray-200 dark:border-gray-600 pb-2">💡 Trend Interpretation</h4>
                                    <div className="text-xs cgm-text-secondary space-y-2">
                                        <p><strong>Recovery:</strong> Positive trends indicate improving fitness and adaptation</p>
                                        <p><strong>Sleep:</strong> Upward trends show better sleep hygiene and recovery practices</p>
                                        <p><strong>Strain:</strong> Monitor increases to prevent overtraining, decreases may indicate detraining</p>
                                    </div>
                                </div>
                                {/* Tooltip Arrow */}
                                <div className={`absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent ${isDark ? 'border-t-gray-800' : 'border-t-white'}`}></div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Recovery Trend */}
                        <div className="bg-green-50 dark:bg-green-900/10 rounded-xl p-4 border border-green-200 dark:border-green-800/30">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold cgm-text-primary">Recovery Trend</h4>
                                <div className={`text-lg ${teamInsights.teamTrends.recoveryTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.recoveryTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                    {teamInsights.teamTrends.recoveryTrend > 0 ? '↗️' : teamInsights.teamTrends.recoveryTrend < 0 ? '↘️' : '➡️'}
                                </div>
                            </div>
                            <div className={`text-2xl font-bold ${teamInsights.teamTrends.recoveryTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.recoveryTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {teamInsights.teamTrends.recoveryTrend > 0 ? '+' : ''}{teamInsights.teamTrends.recoveryTrend.toFixed(1)}
                            </div>
                            <div className="text-xs cgm-text-secondary mt-1">
                                {teamInsights.teamTrends.recoveryTrend > 5 ? 'Improving significantly' :
                                    teamInsights.teamTrends.recoveryTrend > 2 ? 'Gradually improving' :
                                        teamInsights.teamTrends.recoveryTrend > -2 ? 'Stable' :
                                            teamInsights.teamTrends.recoveryTrend > -5 ? 'Slightly declining' : 'Declining significantly'}
                            </div>
                        </div>
                        {/* Sleep Trend */}
                        <div className="bg-purple-50 dark:bg-purple-900/10 rounded-xl p-4 border border-purple-200 dark:border-purple-800/30">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold cgm-text-primary">Sleep Quality Trend</h4>
                                <div className={`text-lg ${teamInsights.teamTrends.sleepTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.sleepTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                    {teamInsights.teamTrends.sleepTrend > 0 ? '↗️' : teamInsights.teamTrends.sleepTrend < 0 ? '↘️' : '➡️'}
                                </div>
                            </div>
                            <div className={`text-2xl font-bold ${teamInsights.teamTrends.sleepTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.sleepTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {teamInsights.teamTrends.sleepTrend > 0 ? '+' : ''}{teamInsights.teamTrends.sleepTrend.toFixed(1)}
                            </div>
                            <div className="text-xs cgm-text-secondary mt-1">
                                {teamInsights.teamTrends.sleepTrend > 3 ? 'Improving significantly' :
                                    teamInsights.teamTrends.sleepTrend > 1 ? 'Gradually improving' :
                                        teamInsights.teamTrends.sleepTrend > -1 ? 'Stable' :
                                            teamInsights.teamTrends.sleepTrend > -3 ? 'Slightly declining' : 'Declining significantly'}
                            </div>
                        </div>
                        {/* Strain Trend */}
                        <div className="bg-orange-50 dark:bg-orange-900/10 rounded-xl p-4 border border-orange-200 dark:border-orange-800/30">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold cgm-text-primary">Training Strain Trend</h4>
                                <div className={`text-lg ${teamInsights.teamTrends.strainTrend > 0 ? 'text-orange-600' : teamInsights.teamTrends.strainTrend < 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                                    {teamInsights.teamTrends.strainTrend > 0 ? '↗️' : teamInsights.teamTrends.strainTrend < 0 ? '↘️' : '➡️'}
                                </div>
                            </div>
                            <div className={`text-2xl font-bold ${teamInsights.teamTrends.strainTrend > 0 ? 'text-orange-600' : teamInsights.teamTrends.strainTrend < 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                                {teamInsights.teamTrends.strainTrend > 0 ? '+' : ''}{teamInsights.teamTrends.strainTrend.toFixed(1)}
                            </div>
                            <div className="text-xs cgm-text-secondary mt-1">
                                {teamInsights.teamTrends.strainTrend > 2 ? 'Increasing intensity' :
                                    teamInsights.teamTrends.strainTrend > 0.5 ? 'Gradual increase' :
                                        teamInsights.teamTrends.strainTrend > -0.5 ? 'Stable workload' :
                                            teamInsights.teamTrends.strainTrend > -2 ? 'Decreasing load' : 'Significantly reduced'}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Athletes In the Red - Critical Alert Section */}
            {teamInsights.athletesInTheRed.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-2xl p-6 shadow-lg">
                    <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center text-white text-xl">
                            🚨
                        </div>
                        <div>
                            <h3 className="text-xl font-bold cgm-text-primary">Athletes Needing Immediate Attention</h3>
                            <p className="text-sm cgm-text-secondary">
                                {teamInsights.athletesInTheRed.length} athlete{teamInsights.athletesInTheRed.length > 1 ? 's are' : ' is'} showing concerning metrics
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {teamInsights.athletesInTheRed.map((athlete: any, index: number) => (
                            <div
                                key={athlete.name}
                                className={`${isDark ? 'bg-gray-800/60' : 'bg-white/80'} rounded-xl p-4 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} hover:scale-105 transition-all duration-200 cursor-pointer`}
                                onClick={() => {
                                    const athleteData = selectedTeam.recovery_data?.athletes_data.find((a: any) => a.athlete_name === athlete.name);
                                    if (athleteData) handleAthleteDoubleClick(athleteData.athlete_id);
                                }}
                            >
                                <div className="flex items-center space-x-3 mb-3">
                                    <div className={`w-10 h-10 ${athlete.severity === 'high' ? 'bg-red-500' : athlete.severity === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'} rounded-full flex items-center justify-center font-bold text-sm cgm-text-primary`}>
                                        {athlete.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold cgm-text-primary text-sm">{athlete.name}</h4>
                                        <div className="flex items-center space-x-2 text-xs">
                                            <span className={`px-2 py-1 rounded-full font-medium cgm-text-primary ${athlete.severity === 'high' ? 'bg-red-500' :
                                                athlete.severity === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'
                                                }`}>
                                                {athlete.severity.toUpperCase()}
                                            </span>
                                            <span className="cgm-text-secondary">{athlete.issues.length} issues</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    {athlete.issues.slice(0, 3).map((issue: string, idx: number) => (
                                        <div key={idx} className="flex items-center space-x-2 text-xs">
                                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                                            <span className="cgm-text-secondary">{issue}</span>
                                        </div>
                                    ))}
                                    {athlete.issues.length > 3 && (
                                        <div className="text-xs cgm-text-secondary text-right">
                                            +{athlete.issues.length - 3} more issues
                                        </div>
                                    )}
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-200/50 grid grid-cols-3 gap-2 text-xs">
                                    <div className="text-center">
                                        <div className="font-medium cgm-text-secondary">Recovery</div>
                                        <div className="font-bold cgm-text-primary" style={{ color: getRecoveryColor(athlete.latestRecovery) }}>{athlete.latestRecovery.toFixed(0)}%</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="font-medium cgm-text-secondary">HRV</div>
                                        <div className="font-bold cgm-text-primary" style={{ color: '#F5D76E' }}>{athlete.latestHRV > 0 ? `${athlete.latestHRV.toFixed(0)}ms` : 'N/A'}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="font-medium cgm-text-secondary">Sleep</div>
                                        <div className="font-bold cgm-text-primary" style={{ color: getSleepColor() }}>{athlete.averageSleep > 0 ? `${athlete.averageSleep.toFixed(0)}%` : 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Key Insights & Action Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Key Insights */}
                <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-lg">
                            💡
                        </div>
                        <h3 className="text-lg font-bold cgm-text-primary">Key Insights</h3>
                    </div>

                    {teamInsights.keyInsights.length > 0 ? (
                        <div className="space-y-3">
                            {teamInsights.keyInsights.map((insight: string, index: number) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg ${isDark ? "bg-blue-700/20" : "bg-blue-100/40"}`}>
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-sm cgm-text-primary leading-relaxed">{insight}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm cgm-text-secondary">No specific insights available for the current data set.</p>
                    )}
                </div>

                {/* Action Items */}
                <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white text-lg">
                            ✅
                        </div>
                        <h3 className="text-lg font-bold cgm-text-primary">Action Items</h3>
                    </div>

                    {teamInsights.actionItems.length > 0 ? (
                        <div className="space-y-3">
                            {teamInsights.actionItems.map((action: string, index: number) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg ${isDark ? 'bg-green-700/20' : 'bg-green-100/40'}`}>
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-sm cgm-text-primary leading-relaxed">{action}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm cgm-text-secondary">No immediate actions required. Team is performing well.</p>
                    )}
                </div>
            </div>

            {/* Top Performers & Data Quality */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Performers */}
                {teamInsights.topPerformers.length > 0 && (
                    <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center text-white text-lg">
                                🏆
                            </div>
                            <h3 className="text-lg font-bold cgm-text-primary">Top Performers</h3>
                        </div>

                        <div className="space-y-3">
                            {teamInsights.topPerformers.map((performer: any, index: number) => (
                                <div
                                    key={performer.name}
                                    className={`flex items-center space-x-3 p-3 rounded-lg ${isDark ? 'bg-yellow-900/10' : 'bg-yellow-50'} hover:scale-105 transition-all duration-200 cursor-pointer`}
                                    onClick={() => {
                                        const athleteData = selectedTeam.recovery_data?.athletes_data.find((a: any) => a.athlete_name === performer.name);
                                        if (athleteData) handleAthleteDoubleClick(athleteData.athlete_id);
                                    }}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black' :
                                        index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800' :
                                            index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white' :
                                                isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-200' : 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700'
                                        }`}>
                                        {index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-medium cgm-text-secondary text-sm">{performer.name}</div>
                                        <div className="text-xs cgm-text-secondary">
                                            Recovery: {performer.latestRecovery.toFixed(0)}% |
                                            Sleep: {performer.averageSleep > 0 ? `${performer.averageSleep.toFixed(0)}%` : 'N/A'} |
                                            Trend: {performer.recoveryTrend > 0 ? '+' : ''}{performer.recoveryTrend.toFixed(1)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Data Quality Issues */}
                {teamInsights.dataQualityIssues.length > 0 && (
                    <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-white text-lg">
                                ⚠️
                            </div>
                            <h3 className="text-lg font-bold cgm-text-primary">Data Quality Issues</h3>
                        </div>

                        <div className="space-y-3">
                            {teamInsights.dataQualityIssues.map((issue: any, index: number) => (
                                <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-opacity-50 bg-orange-100 dark:bg-orange-900/20">
                                    <div className={`w-3 h-3 rounded-full ${issue.connected ? 'bg-orange-500' : 'bg-red-500'}`}></div>
                                    <div className="flex-1">
                                        <div className="font-medium cgm-text-secondary text-sm">{issue.name}</div>
                                        <div className="text-xs cgm-text-secondary">{issue.issue}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamOverviewTab; 